import qs from "qs";

/**
 * Latest `autoSaveListing` call per scope that has not settled yet: `{ signature, promise }`.
 * One edit can reach the autosave thunk twice within a few milliseconds (blur + mouse-leave, a
 * delayed submit, an unmount flush); a save identical to the latest unsettled one shares its
 * request instead of queueing a second one (GROWTH-6932).
 */
const latestUnsettledByScope = new Map();

/**
 * Identity of a save for duplicate detection: the section payload and command, without the
 * per-call ordering metadata. Saves that carry an abort controller are never merged, because the
 * caller expects to cancel its own request; they get a null signature.
 *
 * @param {Object} field Section payload
 * @param {string} command api-v2 save command
 * @param {AbortController} [abortController]
 * @returns {string|null}
 */
export const buildAutosaveSignature = (field, command, abortController) => (abortController ? null : qs.stringify({ ...field, command }));

/**
 * The promise of the latest unsettled save in `scope` when it has the same signature.
 *
 * @param {string} scope
 * @param {string|null} signature
 * @returns {Promise|null}
 */
export const findDuplicateAutosave = (scope, signature) => {
  const latest = latestUnsettledByScope.get(scope);
  return signature !== null && latest?.signature === signature ? latest.promise : null;
};

/**
 * Makes a save the latest one in `scope`, replacing whatever was there.
 *
 * @param {string} scope
 * @param {string|null} signature
 * @returns {{ register: (promise: Promise) => Promise, settle: () => void }}
 *   `register` is called once the save's promise exists, so a duplicate can never be handed a
 *   null promise. `settle` forgets the save when it has settled, unless a newer one replaced it.
 */
export const trackLatestAutosave = (scope, signature) => {
  const entry = { signature, promise: null };

  return {
    register: promise => {
      entry.promise = promise;
      latestUnsettledByScope.set(scope, entry);
      return promise;
    },
    settle: () => {
      if (latestUnsettledByScope.get(scope) === entry) {
        latestUnsettledByScope.delete(scope);
      }
    }
  };
};

/**
 * Forgets every remembered save. The record is module-level, so tests call this between cases;
 * otherwise a save one test leaves unsettled would be shared with the next test's identical save.
 */
export const resetAutosaveDuplicates = () => {
  latestUnsettledByScope.clear();
};
